import { useState, useEffect } from 'react'

export interface HeroImage {
  id: number
  url: string
  order: number
}

const STORAGE_KEY = 'forax_hero_images'

// Default images if none exist in storage
const INITIAL_IMAGES: HeroImage[] = [
  {
    id: 1,
    url: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=2000',
    order: 0,
  },
  {
    id: 2,
    url: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&q=80&w=2000',
    order: 1,
  },
  {
    id: 3,
    url: 'https://images.unsplash.com/photo-1600607686527-6fb886090705?auto=format&fit=crop&q=80&w=2000',
    order: 2,
  },
]

export function useHeroImages() {
  const [images, setImages] = useState<HeroImage[]>(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        return JSON.parse(stored)
      } catch {
        return INITIAL_IMAGES
      }
    }
    return INITIAL_IMAGES
  })

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(images))
  }, [images])

  const addImage = (url: string) => {
    if (images.length >= 4) return // Max 4 images enforcement

    const newId = Math.max(...images.map((img) => img.id || 0), 0) + 1
    const newOrder = images.length

    setImages([...images, { id: newId, url, order: newOrder }])
  }

  const deleteImage = (id: number) => {
    const filtered = images.filter((img) => img.id !== id)
    // Reorder remaining images
    const reordered = filtered.map((img, index) => ({ ...img, order: index }))
    setImages(reordered)
  }

  const moveImage = (id: number, direction: 'up' | 'down') => {
    const index = images.findIndex((img) => img.id === id)
    if (index === -1) return
    if (direction === 'up' && index === 0) return
    if (direction === 'down' && index === images.length - 1) return

    const newImages = [...images]
    const targetIndex = direction === 'up' ? index - 1 : index + 1

    // Swap
    const temp = newImages[index]
    newImages[index] = newImages[targetIndex]
    newImages[targetIndex] = temp

    // Update order property
    newImages.forEach((img, idx) => {
      img.order = idx
    })

    setImages(newImages)
  }

  return {
    images,
    addImage,
    deleteImage,
    moveImage,
    canAdd: images.length < 4,
  }
}
